import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, Shield, CheckCircle2, AlertTriangle, XCircle, Wand2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import TopNav from "../components/TopNav";

const DIFFICULTY_META = {
  junior: { color: "var(--green)", label: "JUNIOR" },
  intermediate: { color: "var(--cyan)", label: "INTERMEDIATE" },
  senior: { color: "var(--amber)", label: "SENIOR" },
  expert: { color: "var(--red)", label: "EXPERT" },
};

const TYPE_LABEL = {
  sigma: "SIGMA RULE",
  kql: "KQL / KUSTO",
  yara: "YARA",
  spl: "SPLUNK SPL",
  ioc: "IOC EXTRACTION",
  mitre: "MITRE ATTRIBUTION",
};

function VerdictIcon({ verdict, size = 14 }) {
  if (verdict === "PASS") return <CheckCircle2 size={size} color="var(--green)" strokeWidth={1.5} />;
  if (verdict === "PARTIAL") return <AlertTriangle size={size} color="var(--amber)" strokeWidth={1.5} />;
  return <XCircle size={size} color="var(--red)" strokeWidth={1.5} />;
}

export default function Practical() {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ track: "operator", difficulty: "intermediate", focus: "" });
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState("");

  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";

  const loadList = async () => {
    try {
      const res = await api.get("/practical/exercises");
      setExercises(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setGenBusy(true); setGenErr("");
    try {
      const body = { track: genForm.track, difficulty: genForm.difficulty };
      if (genForm.focus.trim()) body.focus = genForm.focus.trim();
      await api.post("/scenarios/generate", body);
      setGenOpen(false);
      setGenForm({ track: "operator", difficulty: "intermediate", focus: "" });
      await loadList();
    } catch (e2) {
      setGenErr(e2?.response?.data?.detail || "Sentinel could not generate. Try again.");
    } finally {
      setGenBusy(false);
    }
  };

  const visible = filter === "all"
    ? exercises
    : filter === "generated"
    ? exercises.filter((e) => e.generated)
    : exercises.filter((e) => e.difficulty === filter);

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline">// PRACTICAL_RANGE</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight mt-2">
              AI-Graded Practical Exercises
            </h1>
            <p className="text-[var(--text-2)] text-sm mt-3 max-w-3xl">
              Write detection logic, attribute attacker techniques, extract indicators. Sentinel grades your submission
              against a strict rubric — score, strengths, gaps, and concrete next steps in under 15 seconds.
            </p>
          </div>
          {isLeader && (
            <button onClick={() => setGenOpen(true)} data-testid="gen-scenario-btn"
                    className="btn-primary inline-flex items-center gap-2 text-xs whitespace-nowrap">
              <Wand2 size={14} /> Generate Scenario
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-6 overflow-x-auto" data-testid="difficulty-filter">
          {["all", "junior", "intermediate", "senior", "expert", "generated"].map((k) => {
            const isGen = k === "generated";
            const genCount = exercises.filter((e) => e.generated).length;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
                className={`font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 border whitespace-nowrap inline-flex items-center gap-1.5 ${
                  filter === k
                    ? "bg-[var(--cyan)] text-[var(--bg)] border-[var(--cyan)]"
                    : isGen
                    ? "text-[var(--cyan)] border-[var(--cyan)]/40 hover:border-[var(--cyan)]"
                    : "text-[var(--text-2)] border-[var(--border)] hover:text-[var(--cyan)] hover:border-[var(--cyan)]"
                }`}
              >
                {isGen && <Wand2 size={10} strokeWidth={1.5} />}
                {isGen ? `Sentinel · ${genCount}` : k}
              </button>
            );
          })}
        </div>

        {loading && <div className="text-[var(--text-2)] font-mono text-sm">// Loading practical range…</div>}

        <div className="grid md:grid-cols-2 gap-4" data-testid="exercise-grid">
          {visible.map((ex) => {
            const meta = DIFFICULTY_META[ex.difficulty] || DIFFICULTY_META.junior;
            return (
              <Link
                key={ex.slug}
                to={`/practical/${ex.slug}`}
                data-testid={`exercise-card-${ex.slug}`}
                className="flat-card p-5 hover:border-[var(--cyan)] transition-colors group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span
                    className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border"
                    style={{ color: meta.color, borderColor: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <div className="flex items-center gap-2">
                    {ex.generated && (
                      <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border inline-flex items-center gap-1"
                            style={{ color: "var(--cyan)", borderColor: "var(--cyan)" }}
                            data-testid={`gen-badge-${ex.slug}`}>
                        <Wand2 size={9} strokeWidth={1.5} /> SENTINEL
                      </span>
                    )}
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
                      {TYPE_LABEL[ex.exercise_type] || ex.exercise_type?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <h3 className="font-display font-bold text-lg uppercase tracking-tight group-hover:text-[var(--cyan)] transition-colors mb-2">
                  {ex.title}
                </h3>

                <p className="text-sm text-[var(--text-2)] mb-4 line-clamp-2">{ex.summary}</p>

                <div className="flex items-center justify-between border-t border-[var(--border)] pt-3">
                  <div className="flex items-center gap-3 text-[var(--text-2)] font-mono text-xs">
                    <span className="inline-flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{ex.estimated_minutes}m</span>
                    {ex.mitre_techniques?.length > 0 && (
                      <span className="inline-flex items-center gap-1"><Shield size={11} strokeWidth={1.5} />{ex.mitre_techniques.length} MITRE</span>
                    )}
                  </div>
                  {ex.best ? (
                    <div className="flex items-center gap-1.5 font-mono text-xs" data-testid={`best-score-${ex.slug}`}>
                      <VerdictIcon verdict={ex.best.verdict} />
                      <span style={{ color: ex.best.score >= 70 ? "var(--green)" : ex.best.score >= 40 ? "var(--amber)" : "var(--red)" }}>
                        {ex.best.score}
                      </span>
                      <span className="text-[var(--text-2)]">best</span>
                    </div>
                  ) : (
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] inline-flex items-center gap-1">
                      <Sparkles size={11} strokeWidth={1.5} />Not Attempted
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {!loading && visible.length === 0 && (
          <div className="text-[var(--text-2)] font-mono text-sm">No exercises match this filter.</div>
        )}
      </main>

      {genOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
             onClick={() => setGenOpen(false)} data-testid="gen-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md flat-card p-7 relative">
            <button onClick={() => setGenOpen(false)} className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white" data-testid="gen-modal-close">✕</button>
            <div className="flex items-center gap-2"><Wand2 size={18} color="var(--cyan)" /><div className="overline">// SENTINEL_GENERATE</div></div>
            <h2 className="font-display font-bold text-2xl uppercase mt-1">Mint a fresh scenario</h2>
            <p className="text-sm text-[var(--text-2)] mt-2">Sentinel writes a realistic defensive-cyber exercise tailored to a track + difficulty.</p>
            <form onSubmit={generate} className="mt-5 space-y-4">
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Track</label>
                <select value={genForm.track} onChange={(e) => setGenForm({ ...genForm, track: e.target.value })}
                        className="input-tac mt-1" data-testid="gen-track">
                  <option value="operator">Operator</option>
                  <option value="engineer">Engineer</option>
                  <option value="hunter">Hunter / Analyst</option>
                  <option value="leader">Leader</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Difficulty</label>
                <select value={genForm.difficulty} onChange={(e) => setGenForm({ ...genForm, difficulty: e.target.value })}
                        className="input-tac mt-1" data-testid="gen-difficulty">
                  <option value="junior">Junior</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="senior">Senior</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Focus (optional)</label>
                <input value={genForm.focus} onChange={(e) => setGenForm({ ...genForm, focus: e.target.value })}
                       className="input-tac mt-1" placeholder="ransomware containment, lateral movement…" data-testid="gen-focus" />
                <p className="font-mono text-[10px] text-[var(--text-2)] mt-1">Steers the scenario topic. Leave blank for Sentinel's choice.</p>
              </div>
              {genErr && <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="gen-error">{genErr}</div>}
              <button type="submit" disabled={genBusy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="gen-submit">
                {genBusy ? <><Loader2 size={14} className="animate-spin" /> Sentinel writing…</> : "Generate"}
              </button>
              <p className="font-mono text-[10px] text-[var(--text-2)] text-center">// ~15-30s to mint · appears in catalog when done</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
